<!-- src/layout/BatchRemarkInput.vue -->
<!-- 批量添加弹窗的备注输入子组件（备注框 + 快捷标签 + 字数计数） -->
<!--
  为什么单独抽组件：TiktokQuery 页面组件模板规模很大（数千节点），
  备注框直接 v-model 父组件 data 时，每次按键都会触发整页 render + 全量 diff，
  连续打字明显卡顿。抽成本组件后：
  1. 打字过程只更新组件内部的 localValue，重渲染只发生在本组件（几十个节点）；
  2. 值通过 v-model 协议（update:modelValue 事件）防抖 300ms 回传父组件，
     停顿或失焦时才同步，父组件在连续打字期间完全不重渲染；
  3. 点击快捷标签 / 失焦 / 组件销毁前会立即同步（flush），不丢字。
-->
<template>
  <div class="batch-remark-section">
    <p class="batch-remark-label">备注（可留空）</p>
    <Input
        v-model="localValue"
        type="textarea"
        :rows="2"
        :maxlength="maxlength"
        placeholder="补充具体信息，如：报价 600、已寄样品、约定 11 月 8 日发布、达人返回修改意见"
        @on-blur="flush"
    />
    <div class="batch-remark-foot">
      <div class="batch-remark-tags">
        <span
            v-for="tag in tags"
            :key="tag"
            class="batch-remark-tag"
            @click="appendTag(tag)"
        >{{ tag }}</span>
      </div>
      <span class="batch-remark-counter">{{ localValue.length }}/{{ maxlength }}</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BatchRemarkInput',
  props: {
    // v-model 绑定值（父组件的 batchAddRemark）
    modelValue: { type: String, default: '' },
    // 快捷标签列表
    tags: { type: Array, default: () => [] },
    // 备注最大长度
    maxlength: { type: Number, default: 500 },
    // 防抖毫秒数：连续打字期间不触发父组件重渲染
    debounce: { type: Number, default: 300 }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      // 组件内部值：打字过程中只更新它（仅本组件局部渲染）
      localValue: this.modelValue || '',
      // 防抖定时器句柄（非响应式用途，挂 data 仅为方便清理）
      debounceTimer: null
    };
  },
  watch: {
    // 外部值变化（如打开弹窗清空备注）时同步到内部
    modelValue(newVal) {
      if ((newVal || '') !== this.localValue) {
        this.localValue = newVal || '';
      }
    },
    // 内部值变化 → 防抖回传父组件（打字停顿 300ms 后才让父组件重渲染）
    localValue(newVal) {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.flush(), this.debounce);
    }
  },
  beforeUnmount() {
    // 组件销毁前把未同步的值刷出去，避免丢字，并清理定时器
    this.flush();
  },
  methods: {
    /**
     * 立即把本地值回传父组件（防抖到期 / 失焦 / 标签点击 / 销毁前调用）
     */
    flush() {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = null;
      }
      this.$emit('update:modelValue', this.localValue);
    },
    /**
     * 追加快捷标签：去重 + 中文逗号衔接，并立即回传父组件
     * @param {string} tag 标签文本
     */
    appendTag(tag) {
      const current = this.localValue.trim();
      // 已包含该标签则不重复追加
      if (current.includes(tag)) {
        this.$Message.info(`备注中已有「${tag}」`);
        return;
      }
      // 非空时用中文逗号衔接
      this.localValue = current ? `${current}，${tag}` : tag;
      // 超长截断（与 maxlength 保持一致）
      if (this.localValue.length > this.maxlength) {
        this.localValue = this.localValue.substring(0, this.maxlength);
      }
      this.flush();
    }
  }
};
</script>

<style scoped>
.batch-remark-section {
  margin-bottom: 16px;
}
.batch-remark-label {
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 600;
  opacity: 0.85;
}
.batch-remark-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 8px;
}
.batch-remark-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.batch-remark-tag {
  padding: 2px 10px;
  border: 1px solid rgba(128, 140, 180, 0.45);
  border-radius: 999px;
  background: rgba(128, 140, 180, 0.10);
  color: #7a8699;
  font-size: 12px;
  line-height: 18px;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s ease;
}
.batch-remark-tag:hover {
  border-color: #667eea;
  color: #667eea;
  background: rgba(102, 126, 234, 0.10);
}
.batch-remark-counter {
  flex-shrink: 0;
  font-size: 12px;
  color: #c5c8ce;
}
/* 星空等深色主题下的弹窗内文字（Modal 挂 body，body 也带主题类） */
body.theme-starry .batch-remark-label {
  color: rgba(230, 238, 255, 0.9);
}
body.theme-starry .batch-remark-tag {
  color: rgba(190, 202, 230, 0.85);
  border-color: rgba(150, 165, 200, 0.4);
  background: rgba(150, 165, 200, 0.10);
}
body.theme-starry .batch-remark-tag:hover {
  color: #8fa4ff;
  border-color: rgba(143, 164, 255, 0.7);
}
body.theme-starry .batch-remark-counter {
  color: rgba(170, 182, 210, 0.55);
}
</style>
